import { useMemo } from 'react';

import { usePlayerStats } from '@hooks/usePlayerStats';
import type {
  PlayerStatsAggregation,
  PlayerStatsPosition,
  PlayerStatsRow,
  PlayerStatsVenue
} from '@api-service/player-stats';
import { readStat } from '@lib/page-utils/player-profile/metrics';
import {
  buildStatRange,
  type PositionRangeLookup,
  type StatRange
} from '@lib/page-utils/PlayerComparison/spider';

/**
 * The stats endpoint caps `limit` at 1000 and no position has anywhere near
 * that many players, so one request returns a whole position.
 */
const WHOLE_POSITION_LIMIT = 1000;

/**
 * Players who have not set foot on the pitch in the range are excluded, or a
 * bench full of zeros would drag the bottom of every range to nothing. Anyone
 * with a minute counts, because the 5th percentile floor already discounts
 * the part-timers without hiding a position early in the season.
 */
const MINIMUM_MINUTES_FOR_RANGE = 1;

const POSITIONS: PlayerStatsPosition[] = ['GK', 'DEF', 'MID', 'FWD'];

export type UsePositionStatRangesOptions = {
  /** Positions to load. Others are skipped, so a same-position pair costs one request. */
  positions: string[];
  /** Comma-separated field groups, matching the stats endpoint's `fields`. */
  fields: string;
  statPaths: string[];
  aggregation: PlayerStatsAggregation;
  venue: PlayerStatsVenue;
  minGameweek: number;
  maxGameweek: number;
  season: number | null;
  accessToken?: string;
  enabled?: boolean;
};

export type UsePositionStatRangesResult = {
  getRange: PositionRangeLookup;
  isLoading: boolean;
};

const rangesByPath = (
  rows: PlayerStatsRow[],
  statPaths: string[]
): Map<string, StatRange | null> =>
  new Map(
    statPaths.map((path) => [
      path,
      buildStatRange(
        rows
          .map((row) => readStat(row, path))
          .filter((value): value is number => value !== null)
      )
    ])
  );

/**
 * Loads the 5th-to-95th percentile range per stat for each requested position
 * over the same filters as the comparison, so the spider chart can draw each
 * player on a fixed scale for their own position. Hooks cannot run in a loop
 * of variable length, so there is one fixed query per position, switched on
 * by need.
 */
export const usePositionStatRanges = ({
  positions,
  fields,
  statPaths,
  aggregation,
  venue,
  minGameweek,
  maxGameweek,
  season,
  accessToken,
  enabled = true
}: UsePositionStatRangesOptions): UsePositionStatRangesResult => {
  const queries = POSITIONS.map((position) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks -- POSITIONS is a fixed-length constant, so the call order never changes
    usePlayerStats({
      fields,
      aggregation,
      venue,
      minGameweek,
      maxGameweek,
      minMinutes: MINIMUM_MINUTES_FOR_RANGE,
      limit: WHOLE_POSITION_LIMIT,
      position,
      enabled: enabled && positions.includes(position),
      ...(season !== null ? { season } : {}),
      ...(accessToken ? { accessToken } : {})
    })
  );

  const rowsByPosition = queries.map(
    (query) => query.data?.pages.flatMap((page) => page.data) ?? []
  );

  const getRange = useMemo<PositionRangeLookup>(() => {
    const byPosition = new Map(
      POSITIONS.map((position, index) => [
        position,
        rangesByPath(rowsByPosition[index] ?? [], statPaths)
      ])
    );
    return (position, statPath) =>
      byPosition.get(position as PlayerStatsPosition)?.get(statPath) ?? null;
    // statPaths is derived from a module-level axis list, so joining it keeps
    // the dependency comparison by value rather than by reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...rowsByPosition, statPaths.join(',')]);

  const isLoading = queries.some(
    (query, index) =>
      enabled && positions.includes(POSITIONS[index]) && query.isLoading
  );

  return { getRange, isLoading };
};
